import {
    GridHookForm,
    Loading,
    Typogaphy,
    TextField,
    Select,
    RadioGroup,
} from 'vnetwork-ui'
import { PRIVATE_DOMAIN_BILLING_CYCLE } from '@/modules/Contract/constants'
import { getTimeRegister } from '@/modules/Contract/constants/function'
import { getDomainPrices } from '@/modules/Contract/services'
import { Contract, DomainPrice } from '@/modules/Contract/types'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { Path, useFormContext, useWatch, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface MetadataProps {
    rootName: string
    index?: number
    isAddons?: boolean
    isDNS?: boolean
}

const Metadata: React.FC<MetadataProps> = ({
    rootName,
    index,
    isAddons,
    isDNS,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()
    const today = dayjs()

    const { data, isPending } = useQuery({
        queryKey: [`domain_prices`],
        queryFn: getDomainPrices,
    })

    const domain_type = useWatch({
        control: formControl.control,
        name: `${rootName}.domain_type` as Path<Contract>,
        defaultValue: 'register',
    })

    const domain_value = useWatch({
        control: formControl.control,
        name: `${rootName}.domain_value` as Path<Contract>,
    }) as string

    const domain_registrationperiod = useWatch({
        control: formControl.control,
        name: `${rootName}.domain_registrationperiod` as Path<Contract>,
    }) as string

    const getPriceToDomain = (domain_type: string, domain: DomainPrice) => {
        const price =
            domain?.[
                domain_type === 'register'
                    ? 'dp_price_register'
                    : 'dp_price_transfer'
            ] || 0

        return price > 0 ? price : 0
    }

    useEffect(() => {
        if (index === undefined || isNaN(Number(index))) return
        const regaxDomain =
            /^(?=.{4,253}$)(?!\-)(?:[a-zA-Z0-9\-]{1,63}\.)+[a-zA-Z]{2,63}$/
        if (!domain_value?.trim() || !regaxDomain.test(domain_value)) return

        const domains = data?.results?.filter((item) =>
            domain_value?.endsWith(item.dp_extension)
        )

        formControl.setValue(
            `order.products.[${index}].service_price_register` as Path<Contract>,
            getPriceToDomain(
                domain_type,
                domains?.[domains?.length - 1] as DomainPrice
            ) * getTimeRegister(domain_registrationperiod)
        )
    }, [
        domain_value,
        data?.results,
        domain_registrationperiod,
        domain_type,
        index,
    ])

    // tự động điều chỉnh thời hạn hợp đồng thành domain_registrationperiod (Thời Hạn Đăng Ký Domain)
    useEffect(() => {
        formControl.setValue(
            `contract_expiry_date`,
            today
                .add(getTimeRegister(domain_registrationperiod) || 1, 'year')
                .subtract(1, 'day')
        )
    }, [domain_registrationperiod])

    if (isPending) {
        return (
            <div>
                <Loading.CircularProgress width={20} height={20} />
            </div>
        )
    }

    return (
        <div>
            <Typogaphy className="font-medium mb-2" size="medium">
                {t('configuration-information')}
            </Typogaphy>

            <GridHookForm
                subForm
                actions={[]}
                data={[
                    {
                        renderEditComponent: (control) => (
                            <div data-tour="domain-type">
                                <Controller
                                    control={control}
                                    name={
                                        `${rootName}.domain_type` as Path<Contract>
                                    }
                                    defaultValue="register"
                                    render={({ field }) => (
                                        <RadioGroup
                                            {...field}
                                            options={[
                                                {
                                                    value: 'register',
                                                    label: 'Đăng ký mới',
                                                },
                                                {
                                                    value: 'transfer',
                                                    label: 'Đổi nhà cung cấp',
                                                },
                                            ]}
                                            direction="row"
                                        />
                                    )}
                                />
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: (control) => (
                            <div data-tour="domain-name">
                                <Controller
                                    control={control}
                                    name={
                                        `${rootName}.domain_value` as Path<Contract>
                                    }
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t('domain_value')}
                                            placeholder={t('domain_value')}
                                            size="small"
                                        />
                                    )}
                                />
                            </div>
                        ),
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: (control) => (
                            <div data-tour="domain-period">
                                <Controller
                                    control={control}
                                    name={
                                        `${rootName}.domain_registrationperiod` as Path<Contract>
                                    }
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={
                                                PRIVATE_DOMAIN_BILLING_CYCLE
                                            }
                                            label09={t(
                                                'domain_registrationperiod'
                                            )}
                                            placeholder={t(
                                                'domain_registrationperiod'
                                            )}
                                            size="small"
                                        />
                                    )}
                                />
                            </div>
                        ),
                        className: 'col-span-2',
                        hidden: index === undefined || isNaN(Number(index)),
                    },
                    {
                        formItemProps: {
                            name: `order.products.[${index}].service_price_register` as Path<Contract>,
                            type: 'number',
                            label: t('domain.service_price_register'),
                            placeholder: t('domain.service_price_register'),
                            size: 'small',
                        },
                        className: 'col-span-4',
                        hidden: index === undefined || isNaN(Number(index)),
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_eppcode` as Path<Contract>,
                            type: 'textfield',
                            label: t('domain_eppcode'),
                            placeholder: t('domain_eppcode'),
                        },
                        className: 'col-span-4',
                        hidden: domain_type === 'register',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.addons` as Path<Contract>,
                            type: 'checkboxgroup',
                            options: [
                                {
                                    value: 'domain_dns_management',
                                    label: 'DNS Management',
                                },
                                {
                                    value: 'domain_email_forwarding',
                                    label: 'Email Forwarding',
                                },
                                {
                                    value: 'domain_id_protection',
                                    label: 'ID Protection',
                                },
                            ],
                            label: t('domain_addons'),
                            placeholder: t('domain_addons'),
                            direction: 'row',
                            disabled: !isAddons,
                            hidden: !isAddons,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.dns_option` as Path<Contract>,
                            type: 'radiogroup',
                            label: t('dns_option'),
                            placeholder: t('dns_option'),
                            options: [
                                { value: 'default', label: 'DNS mặc định' },
                                { value: 'custom', label: 'DNS khác' },
                            ],
                            direction: 'row',
                            defaultValue: 'default',
                            disabled: !isDNS,
                            hidden: !isDNS,
                        },
                        className: 'col-span-4',
                    },
                ]}
                formControl={formControl}
            />
        </div>
    )
}

export default Metadata
